// app/(main)/positions/[slug]/_components/PositionFilterBar.tsx
"use client";

import { useSearchParams } from "next/navigation";
import { Building2 } from "lucide-react";
import type { OrgOption, PositionSortKey } from "@/actions/positions";

const SORT_OPTIONS: { value: PositionSortKey; label: string }[] = [
  { value: "newest", label: "Mới nhất" },
  { value: "popular", label: "Xem nhiều" },
  { value: "rating", label: "Rating cao" },
];

interface Props {
  positionSlug: string;
  currentTab: "paths" | "exams" | "reviews";
  orgs: OrgOption[];
  selectedOrg: string | null;
  currentSort: PositionSortKey;
}

export default function PositionFilterBar({ positionSlug, currentTab, orgs, selectedOrg, currentSort }: Props) {
  const searchParams = useSearchParams();

  // Hard navigate vì Next.js bug: router.push không trigger re-render khi cùng pathname
  function pushParams(updates: Record<string, string | null>) {
    const params = new URLSearchParams(searchParams.toString());
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") params.delete(key);
      else params.set(key, value);
    }
    params.delete("page");
    if (!params.has("tab")) params.set("tab", currentTab);
    window.location.href = `/positions/${positionSlug}?${params.toString()}`;
  }

  const showOrgFilter = orgs.length > 0;

  return (
    <div className="flex items-center gap-2.5 flex-wrap">
      {showOrgFilter && (
        <div className="flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
          <select
            value={selectedOrg ?? ""}
            onChange={(e) => pushParams({ org: e.target.value || null })}
            className="h-8 rounded-full border border-border bg-white px-3 pr-7 text-[11px] font-semibold text-foreground outline-none cursor-pointer appearance-none bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2210%22%20height%3D%2210%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22/%3E%3C/svg%3E')] bg-no-repeat bg-[right_0.6rem_center] focus:border-teal-600 transition-all"
          >
            <option value="">Tất cả tổ chức</option>
            {orgs.map(o => (
              <option key={o.id} value={o.slug}>
                {o.emoji ? `${o.emoji} ` : ""}{o.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex-1" />

      <div className="flex items-center gap-1.5">
        {SORT_OPTIONS.map(s => (
          <button
            key={s.value}
            onClick={() => pushParams({ sort: s.value === "newest" ? null : s.value })}
            className={`px-3 py-[5px] rounded-full text-[10px] font-semibold border whitespace-nowrap transition-all ${
              currentSort === s.value
                ? "bg-teal-800 text-white border-teal-800"
                : "border-border bg-white text-muted-foreground hover:border-teal-500 hover:text-teal-700"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}